import type { ClientAppName, MembershipSummary } from '../api/types';

/**
 * The app that opens a membership, where it is not this one; undefined where this app can.
 *
 * A membership that suits no app at all is left choosable: there is nowhere better to send it, and the app
 * answers with its own refusal, as it did before the chooser knew.
 */
export function opensElsewhere(membership: MembershipSummary, app: ClientAppName): ClientAppName | undefined {
  // An API from before the chooser knew sends no apps; every option then stays choosable.
  const apps = membership.apps ?? [];
  return apps.length === 0 || apps.includes(app) ? undefined : apps[0];
}

/** The nursery a sign-in from an entry link prefers, and the app it was made in. */
export interface MembershipPreference {
  tenantSlug: string;
  app: ClientAppName;
}

/**
 * The membership a sign-in from a nursery's entry link selects without asking: the one membership at that
 * nursery this app opens. Undefined where there is none, or more than one (a parent who also teaches there
 * chooses, as everyone with several memberships does).
 */
export function preferredMembership(
  memberships: readonly MembershipSummary[], preference: MembershipPreference,
): MembershipSummary | undefined {
  const candidates = memberships.filter((membership) =>
    membership.tenantSlug === preference.tenantSlug && opensElsewhere(membership, preference.app) === undefined);
  return candidates.length === 1 ? candidates[0] : undefined;
}
